/** leads/LeadImportModal.tsx — 단건 등록 + 엑셀 대량 업로드 (가능연도 실시간 미리보기) */
import React, { useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { bulkCreate } from './api';
import type { LeadRowInput } from './types';
import { eligibleYears, parseExcelRows, fmtBizNo } from './calc';
import type { ParsedRow } from './calc';

interface Props { onClose: () => void; onDone: () => void; onToast: (m: string, t?: string) => void }

const EMPTY = { biz_no: '', corp_name: '', ceo_name: '', address: '', phone: '', email: '', invest_year: '', invest_amount: '', venture_from: '', venture_to: '' };

export default function LeadImportModal({ onClose, onDone, onToast }: Props) {
  const [tab, setTab] = useState<'single' | 'excel'>('single');
  const [f, setF] = useState(EMPTY);
  const [invests, setInvests] = useState<{ year: string; amount: string }[]>([{ year: '', amount: '' }]); // 다회 증자 대응
  const [parsed, setParsed] = useState<ParsedRow[]>([]);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const set = (k: keyof typeof EMPTY) => (e: React.ChangeEvent<HTMLInputElement>) => setF((p) => ({ ...p, [k]: e.target.value }));

  const previewYears = eligibleYears(parseInt(invests[0].year, 10), f.venture_from || null, f.venture_to || null);

  const submitSingle = async () => {
    const bizNo = f.biz_no.replace(/[^0-9]/g, '');
    if (bizNo.length !== 10) { onToast('사업자번호는 10자리여야 합니다', 'error'); return; }
    if (!f.corp_name.trim()) { onToast('법인명을 입력하세요', 'error'); return; }
    const rowsIn = invests.map((v) => ({ year: parseInt(v.year, 10), amount: v.amount ? Number(v.amount.replace(/[^0-9]/g, '')) : null })).filter((v) => v.year > 0);
    if (rowsIn.length === 0) { onToast('투자연도(증자연도)를 1건 이상 입력하세요', 'error'); return; }
    if (new Set(rowsIn.map((v) => v.year)).size !== rowsIn.length) { onToast('같은 증자연도가 중복 입력됐습니다', 'error'); return; }
    setBusy(true);
    try {
      const rows: LeadRowInput[] = rowsIn.map((v) => ({
        biz_no: bizNo, corp_name: f.corp_name.trim(), ceo_name: f.ceo_name.trim(),
        address: f.address.trim(), phone: f.phone.trim(), email: f.email.trim(),
        detail: { invest_year: v.year, invest_amount: v.amount, venture_from: f.venture_from || null, venture_to: f.venture_to || null },
      }));
      const r = await bulkCreate({ rows });
      onToast(r.created > 0 ? '등록 완료 ' + r.created + '건' + (r.skipped > 0 ? ' (기존 중복 ' + r.skipped + '건 제외)' : '') : '전부 이미 등록된 리드입니다 (중복)', r.created > 0 ? undefined : 'error');
      if (r.created > 0) { setF(EMPTY); setInvests([{ year: '', amount: '' }]); onDone(); }
    } catch (e) { onToast((e as Error).message, 'error'); } finally { setBusy(false); }
  };

  const downloadTemplate = () => {
    const rows = [
      ['사업자등록번호', '법인등록번호', '법인명', '대표자명', '투자연도', '투자금액', '벤처시작일', '벤처종료일', '주소', '연락처', '이메일', '메모'],
      ['123-45-67890', '1101110000000', '주식회사 예시', '홍길동', 2023, 100000000, '2022-05-01', '2025-04-30', '서울특별시 강남구 테헤란로 1', '02-1234-5678', 'ceo@example.com', '크레탑 확인 완료'],
      ['987-65-43210', '', '(주)샘플전자', '', 2024, '', '', '', '경기도 성남시', '', '', '금액·기간 미상도 등록 가능'],
    ];
    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws['!cols'] = [{ wch: 15 }, { wch: 15 }, { wch: 18 }, { wch: 10 }, { wch: 9 }, { wch: 13 }, { wch: 12 }, { wch: 12 }, { wch: 28 }, { wch: 14 }, { wch: 18 }, { wch: 18 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, '영업DB등록');
    XLSX.writeFile(wb, '영업DB_등록양식.xlsx');
  };

  const onFile = async (file: File) => {
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { cellDates: true });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const objs = XLSX.utils.sheet_to_json<Record<string, unknown>>(ws, { defval: '' });
      const rows = parseExcelRows(objs);
      setParsed(rows);
      const ok = rows.filter((r) => r.row).length;
      onToast('파싱 완료: 유효 ' + ok + '건 / 오류 ' + (rows.length - ok) + '건');
    } catch (e) { onToast('엑셀 파싱 실패: ' + (e as Error).message, 'error'); }
  };

  const submitExcel = async () => {
    const rows = parsed.filter((r) => r.row).map((r) => r.row as LeadRowInput);
    if (rows.length === 0) { onToast('업로드할 유효 행이 없습니다', 'error'); return; }
    setBusy(true);
    try {
      const r = await bulkCreate({ batch_name: new Date().toISOString().slice(0, 10) + ' 엑셀 업로드', rows });
      onToast('등록 ' + r.created + '건 · 중복 스킵 ' + r.skipped + '건');
      onDone();
    } catch (e) { onToast((e as Error).message, 'error'); } finally { setBusy(false); }
  };

  return (
    <div className="ld-modal-bg ld-root" onClick={onClose}>
      <div className="ld-modal wide" onClick={(e) => e.stopPropagation()}>
        <div className="ld-modal-h">
          <span>영업 DB 등록 — 벤처투자소득공제 경정청구</span>
          <button className="ld-btn" onClick={onClose}>닫기</button>
        </div>
        <div className="ld-modal-b">
          <div className="ld-toolbar">
            <button className={'ld-btn' + (tab === 'single' ? ' pri' : '')} onClick={() => setTab('single')}>단건 입력</button>
            <button className={'ld-btn' + (tab === 'excel' ? ' pri' : '')} onClick={() => setTab('excel')}>엑셀 업로드</button>
          </div>

          {tab === 'single' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div className="ld-field"><label>사업자등록번호 *</label><input value={f.biz_no} onChange={set('biz_no')} placeholder="000-00-00000" /></div>
              <div className="ld-field"><label>법인명 *</label><input value={f.corp_name} onChange={set('corp_name')} /></div>
              <div className="ld-field"><label>대표자명</label><input value={f.ceo_name} onChange={set('ceo_name')} /></div>
              <div className="ld-field"><label>연락처</label><input value={f.phone} onChange={set('phone')} /></div>
              <div className="ld-field" style={{ gridColumn: '1 / -1' }}><label>법인주소</label><input value={f.address} onChange={set('address')} /></div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ fontSize: 11, color: '#9CA3AF', fontWeight: 600 }}>투자 내역(자본금 증자) * — 여러 번 증자했으면 전부 입력하세요 (연도별로 각각 관리·DM은 자동 합산)</label>
                {invests.map((v, i) => (
                  <div key={i} style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                    <input style={{ flex: 1 }} value={v.year} placeholder={'증자연도 (예: ' + (2021 + i) + ')'}
                      onChange={(e) => { const t = e.target.value.replace(/[^0-9]/g, ''); setInvests((a) => a.map((x, j) => (j === i ? { ...x, year: t } : x))); }} />
                    <input style={{ flex: 1.4 }} value={v.amount} placeholder="증자금액(원) — 모르면 비워두기"
                      onChange={(e) => { const t = e.target.value; setInvests((a) => a.map((x, j) => (j === i ? { ...x, amount: t } : x))); }} />
                    {invests.length > 1 && <button className="ld-btn" style={{ padding: '6px 12px' }} onClick={() => setInvests((a) => a.filter((_, j) => j !== i))}>−</button>}
                  </div>
                ))}
                {invests.length < 6 && <button className="ld-btn" style={{ marginTop: 6, fontSize: 12 }} onClick={() => setInvests((a) => [...a, { year: '', amount: '' }])}>+ 투자 추가</button>}
              </div>
              <div className="ld-field"><label>벤처 유효기간 시작</label><input type="date" value={f.venture_from} onChange={set('venture_from')} /></div>
              <div className="ld-field"><label>벤처 유효기간 종료</label><input type="date" value={f.venture_to} onChange={set('venture_to')} /></div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ fontSize: 11, color: '#4B5563', fontWeight: 700 }}>경정청구 가능연도 (자동계산)</label>
                <div style={{ marginTop: 4 }}>
                  {invests[0].year && previewYears.length === 0 && <span className="ld-year expired">가능연도 없음 (시효 도과 또는 벤처기간 불일치)</span>}
                  {previewYears.map((y) => (
                    <span key={y.year} className={'ld-year' + (y.daysLeft <= 180 ? ' urgent' : '')}>귀속 {y.year} · 마감 {y.deadline}</span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {tab === 'excel' && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <span style={{ fontSize: 12, color: '#4B5563' }}>필수: 사업자등록번호 · 법인명 · 투자연도(증자연도) — 나머지는 선택</span>
                <button className="ld-btn" onClick={downloadTemplate}>⬇ 엑셀 양식 다운로드</button>
              </div>
              <div className="ld-drop" onClick={() => fileRef.current?.click()}>
                엑셀 파일 선택 (.xlsx) — 아래 양식의 헤더 그대로 사용하세요
                <input ref={fileRef} type="file" accept=".xlsx,.xls" hidden onChange={(e) => { const file = e.target.files?.[0]; if (file) onFile(file); e.target.value = ''; }} />
              </div>
              {parsed.length > 0 && (
                <table className="ld-table" style={{ marginTop: 12 }}>
                  <thead><tr><th>#</th><th>법인명</th><th>사업자번호</th><th>투자연도</th><th>가능연도</th><th>비고</th></tr></thead>
                  <tbody>
                    {parsed.slice(0, 100).map((r, i) => (
                      <tr key={i}>
                        <td>{i + 1}</td>
                        <td>{r.row ? r.row.corp_name : String(r.raw['법인명'] || r.raw['기업명'] || '-')}</td>
                        <td>{r.row ? fmtBizNo(r.row.biz_no) : '-'}</td>
                        <td>{r.row ? r.row.detail.invest_year : '-'}</td>
                        <td>{r.row ? eligibleYears(r.row.detail.invest_year, r.row.detail.venture_from, r.row.detail.venture_to).map((y) => y.year).join(', ') || '없음' : '-'}</td>
                        <td>{r.error ? <span className="ld-err">{r.error}</span> : '✓'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {parsed.length > 100 && <div style={{ fontSize: 12, color: '#4B5563', marginTop: 6 }}>미리보기는 100행까지 표시 (전체 {parsed.length}행 업로드됨)</div>}
            </>
          )}
        </div>
        <div className="ld-modal-f">
          {tab === 'single'
            ? <button className="ld-btn pri" disabled={busy} onClick={submitSingle}>{busy ? '등록 중…' : '등록'}</button>
            : <button className="ld-btn pri" disabled={busy || parsed.filter((r) => r.row).length === 0} onClick={submitExcel}>{busy ? '업로드 중…' : '유효 ' + parsed.filter((r) => r.row).length + '건 등록'}</button>}
        </div>
      </div>
    </div>
  );
}
